import type { FootballGameWinnerType, TeamNum } from "./types.ts";

export const FORCE_GAME_WINNER_OPTIONS: readonly {
	value: "" | FootballGameWinnerType;
	label: string;
}[] = [
	{ value: "", label: "Any scoring play" },
	{ value: "kicker", label: "Kicker field goal" },
	{ value: "returnTD", label: "Return touchdown" },
	{ value: "rushTD", label: "Rushing touchdown" },
	{ value: "hailMaryTD", label: "Hail Mary touchdown" },
];

const FOOTBALL_GAME_WINNER_TYPES = new Set<FootballGameWinnerType>([
	"kicker",
	"returnTD",
	"rushTD",
	"hailMaryTD",
]);

export const isFootballGameWinnerType = (
	value: unknown,
): value is FootballGameWinnerType => {
	return (
		typeof value === "string" &&
		FOOTBALL_GAME_WINNER_TYPES.has(value as FootballGameWinnerType)
	);
};

export type ForcedGameWinnerPlayType =
	| "kickoff"
	| "punt"
	| "fieldGoal"
	| "run"
	| "pass";

export type FootballOvertimeState =
	| "initialKickoff"
	| "firstPossession"
	| "secondPossession"
	| "bothTeamsPossessed"
	| "over";

type ForcedGameWinnerPlayParams = {
	forceWinTeam: TeamNum | undefined;
	forceWinType: FootballGameWinnerType | undefined;
	offense: TeamNum;
	defense: TeamNum;
	awaitingKickoff: boolean;
	quarter: number;
	numPeriods: number;
	clock: number;
	scrimmage: number;
	overtimeState: FootballOvertimeState | undefined;
	overtimeType: "suddenDeath" | "exceptFg" | "bothPossess";
	points: [number, number];
};

// These are deliberately kept here rather than embedded in GameSim so the
// timing and setup rules can be changed without touching the play mechanics.
export const FORCE_GAME_WINNER_SETUP_TIME = 2;
export const FORCE_GAME_WINNER_SCORE_TIME = 2;
export const MAX_FORCED_FIELD_GOAL_DISTANCE = 69;

const canEndOvertimeWithType = ({
	forceWinType,
	overtimeState,
	overtimeType,
}: Pick<
	ForcedGameWinnerPlayParams,
	"forceWinType" | "overtimeState" | "overtimeType"
>) => {
	if (overtimeState === undefined) {
		return true;
	}

	if (
		overtimeState === "secondPossession" ||
		overtimeState === "bothTeamsPossessed"
	) {
		return true;
	}

	if (overtimeType === "suddenDeath") {
		return true;
	}

	// Under the "except field goal" rule, a touchdown can end the first
	// possession, but a field goal cannot.
	return forceWinType !== "kicker";
};

/**
 * Return the play that should be forced right at the end of a game, if any.
 * This only selects a play; the Football GameSim remains responsible for
 * producing normal events and statistics for that play.
 */
export const getForcedGameWinnerPlayType = ({
	forceWinTeam,
	forceWinType,
	offense,
	defense,
	awaitingKickoff,
	quarter,
	numPeriods,
	clock,
	scrimmage,
	overtimeState,
	overtimeType,
	points,
}: ForcedGameWinnerPlayParams): ForcedGameWinnerPlayType | undefined => {
	if (
		forceWinTeam === undefined ||
		forceWinType === undefined ||
		quarter < numPeriods ||
		overtimeState === "over" ||
		!canEndOvertimeWithType({
			forceWinType,
			overtimeState,
			overtimeType,
		})
	) {
		return undefined;
	}

	const pointsDown = points[forceWinTeam === 0 ? 1 : 0] - points[forceWinTeam];
	// A touchdown must put the selected team ahead by itself. The extra point
	// is intentionally not part of the forced game-winning play.
	const maxPoints = forceWinType === "kicker" ? 3 : 5;
	if (pointsDown < 0 || pointsDown > maxPoints) {
		return undefined;
	}

	if (awaitingKickoff) {
		return forceWinType === "returnTD" && defense === forceWinTeam
			? "kickoff"
			: undefined;
	}

	// First make sure the selected team gets the ball. For a return TD, the
	// selected team must punt first if it currently has possession, so the next
	// punt can be returned by the selected team.
	if (clock > FORCE_GAME_WINNER_SETUP_TIME) {
		return undefined;
	}

	if (forceWinType === "returnTD") {
		return "punt";
	}

	if (offense !== forceWinTeam) {
		return "punt";
	}

	if (clock > FORCE_GAME_WINNER_SCORE_TIME) {
		return undefined;
	}

	if (forceWinType === "kicker") {
		const fieldGoalDistance = 117 - scrimmage;
		if (fieldGoalDistance > MAX_FORCED_FIELD_GOAL_DISTANCE) {
			return undefined;
		}
		return "fieldGoal";
	}
	if (forceWinType === "rushTD") {
		return "run";
	}
	return "pass";
};

type ScoringSummaryEvent = {
	type?: unknown;
	t?: unknown;
	quarter?: unknown;
	td?: unknown;
	made?: unknown;
	safety?: unknown;
	hailMary?: unknown;
	twoPointConversionTeam?: unknown;
};

const getScoringType = (
	event: ScoringSummaryEvent,
): FootballGameWinnerType | "other" | undefined => {
	if (event.type === "fieldGoal") {
		return event.made === true ? "kicker" : undefined;
	}

	if (event.safety === true) {
		return "other";
	}

	if (event.td !== true) {
		return undefined;
	}

	if (event.type === "kickoffReturn" || event.type === "puntReturn") {
		return "returnTD";
	}
	if (event.type === "run") {
		return "rushTD";
	}
	if (event.type === "passComplete") {
		return event.hailMary === true ? "hailMaryTD" : "other";
	}

	return "other";
};

const getScoringPoints = (event: ScoringSummaryEvent) => {
	if (event.type === "fieldGoal") {
		return event.made === true ? 3 : 0;
	}
	if (event.type === "extraPoint") {
		return event.made === true ? 1 : 0;
	}
	if (event.td === true) {
		return event.twoPointConversionTeam === undefined ? 6 : 2;
	}
	if (event.safety === true) {
		return 2;
	}

	return 0;
};

const getScoringTeam = (event: ScoringSummaryEvent): TeamNum | undefined => {
	if (event.t !== 0 && event.t !== 1) {
		return undefined;
	}

	return event.safety === true ? (event.t === 0 ? 1 : 0) : event.t;
};

/**
 * Check the final meaningful scoring play while ignoring extra points and
 * missed/failed attempts that are also present in the scoring summary.
 */
export const matchesForcedGameWinner = ({
	forceWinTeam,
	forceWinType,
	scoringSummary,
	finalPeriod = 4,
}: {
	forceWinTeam: number | undefined;
	forceWinType: FootballGameWinnerType | undefined;
	scoringSummary: unknown;
	finalPeriod?: number;
}) => {
	if (forceWinType === undefined) {
		return true;
	}
	if (!Array.isArray(scoringSummary) || forceWinTeam === undefined) {
		return false;
	}

	for (let i = scoringSummary.length - 1; i >= 0; i -= 1) {
		const event = scoringSummary[i] as ScoringSummaryEvent;
		const scoringType = getScoringType(event);
		if (scoringType === undefined) {
			continue;
		}

		if (scoringType !== forceWinType || getScoringPoints(event) <= 0) {
			return false;
		}
		if (typeof event.quarter === "number" && event.quarter < finalPeriod) {
			return false;
		}

		const scores: [number, number] = [0, 0];
		for (let j = 0; j < i; j += 1) {
			const previousEvent = scoringSummary[j] as ScoringSummaryEvent;
			const previousTeam = getScoringTeam(previousEvent);
			const previousPoints = getScoringPoints(previousEvent);
			if (previousTeam !== undefined && previousPoints > 0) {
				scores[previousTeam] += previousPoints;
			}
		}

		const scoringTeam = getScoringTeam(event);
		if (scoringTeam !== forceWinTeam) {
			return false;
		}

		const otherTeam = scoringTeam === 0 ? 1 : 0;
		return (
			scores[scoringTeam] <= scores[otherTeam] &&
			scores[scoringTeam] + getScoringPoints(event) > scores[otherTeam]
		);
	}

	return false;
};
